import { AuthShowcase } from "../components/AuthShowcase";
import { LoginHelplines } from "../features/wellbeing/HelplineBanner";
import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLoginMutation } from "../services/api";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setTokens } from "../features/auth/authSlice";
import { setTenant } from "../features/tenant/tenantSlice";
import { deriveTenantSlug, SUPER_ADMIN_SLUG, useTenantBootstrap } from "../hooks/useTenant";
import { decodeTokenClaims, getDefaultRouteForRole } from "../features/auth/tokenUtils";
import { logFrontendError } from "../services/errorReporter";

/**
 * What the sign-in form says when it fails. A 403 is a right password on an
 * account that may not come in yet (a registration waiting for approval, a
 * refused one, a suspended institute): the server says why, so show that.
 * Anything else stays the generic line, which reveals nothing about the email.
 */
export const loginErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  const failure = error as { status?: number; data?: { detail?: unknown } };
  if (failure.status === 403 && typeof failure.data?.detail === "string") return failure.data.detail;
  return "Check credentials and try again.";
};

/**
 * Presents the branded login surface for every tenant.
 * Why: centralizes authentication so we can consistently hydrate Redux and redirect based on role.
 */
export const LoginPage = () => {
  useTenantBootstrap();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const dispatch = useAppDispatch();
  const tenantBranding = useAppSelector((state) => state.tenant.branding);
  const [login, { isLoading, error }] = useLoginMutation();
  const navigate = useNavigate();

  const resolvedTenantSlug = tenantBranding?.tenant_slug ?? deriveTenantSlug(
    typeof window !== "undefined" ? window.location.hostname : SUPER_ADMIN_SLUG
  );
  const isPlatformTenant = resolvedTenantSlug === SUPER_ADMIN_SLUG;
  const resolveRoleFromSession = (accessToken: string, fallbackRole?: string | null): string | null => {
    if (fallbackRole) {
      return fallbackRole;
    }
    try {
      const claims = decodeTokenClaims(accessToken);
      return typeof claims.role === "string" ? claims.role : null;
    } catch (error) {
      console.warn("Unable to decode access token while determining redirect", error);
      return null;
    }
  };
  /**
   * Handles the form post to login and orchestrates post-auth redirects.
   * Why: keeps navigation logic colocated with the form so we can ensure a single code path for all tenants.
   */
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      const session = await login({ email, password }).unwrap();
      dispatch(
        setTokens({ access: session.tokens.access_token, refresh: session.tokens.refresh_token, user: session.user })
      );
      const tenantFromSession = session.user?.tenant_slug ?? resolvedTenantSlug;
      if (tenantFromSession) {
        dispatch(setTenant({ slug: tenantFromSession }));
      }
      const resolvedRole = resolveRoleFromSession(session.tokens.access_token, session.user?.role);
      const nextRoute = getDefaultRouteForRole(resolvedRole);
      // Always the dashboard after signing in — only an invite link (which saves
      // its paper as `postLoginRedirect`) still takes the student to that paper.
      // The "page you tried to open before login" hint is no longer followed:
      // it is what landed students on different pages each time.
      const redirectHint = sessionStorage.getItem("postLoginRedirect");
      if (redirectHint) {
        sessionStorage.removeItem("postLoginRedirect");
        navigate(redirectHint, { replace: true });
        return;
      }
      navigate(nextRoute, { replace: true });
    } catch (submitError) {
      await logFrontendError("LoginPage.handleSubmit", "Login flow failed", submitError, {
        email
      });
    }
  };

  const formError = loginErrorMessage(error);
  // A real coaching's name is a welcome sign that you are in the right
    // place. The platform tenant's own name is not — it resolves to the
    // super-admin org on any non-subdomain host, so every student reaching
    // the plain login URL was greeted with internal naming.
    const brandName = isPlatformTenant ? "Green Agentick" : (tenantBranding?.display_name ?? "Green Agentick");
  // Never print the raw tenant slug here: on a plain host it resolves to
  // the platform pseudo-tenant and read as "platform · secure sign in".
  const heroEyebrow = useMemo(
    () => (isPlatformTenant ? "Secure sign in" : `${resolvedTenantSlug} · secure sign in`),
    [isPlatformTenant, resolvedTenantSlug]
  );

  return (
    <div className="login-shell">
      <section className="login-hero">
        <Link to="/" className="login-hero__brand">{brandName}</Link>
        <div className="login-hero__center">
          <p className="login-hero__eyebrow">{heroEyebrow}</p>
          <h1>Good to see you again.<br />Let's keep going.</h1>
          <AuthShowcase variant="login" />
        </div>
        <p className="login-hero__foot">© {new Date().getFullYear()} {brandName}</p>
      </section>

      <section className="login-panel">
        <div className="login-card" aria-live="polite">
          <div className="login-card__header">
            {/* Phones hide the ink hero panel, which takes the wordmark with it.
                This keeps the brand (and a route home) on the card itself. */}
            <Link to="/" className="login-card__brand">{brandName}</Link>
            <h2 className="login-card__title">Welcome back</h2>
            <p className="auth-card__description">Sign in to continue your preparation.</p>
          </div>
          <form className="auth-form" onSubmit={handleSubmit}>
            <label className="form-field">
              <span className="form-label">Email address</span>
              <input
                className="text-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label className="form-field">
              <span className="form-label">Password</span>
              <input
                className="text-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <div className="auth-form__actions">
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? "Signing in..." : "Sign in"}
              </button>
              <p className="form-hint">
                Need an account? <Link to="/register">Register as a student</Link>
              </p>
            </div>
            {formError && <p className="form-error" role="alert">{formError}</p>}
          </form>
          {!isPlatformTenant && resolvedTenantSlug ? <LoginHelplines slug={resolvedTenantSlug} /> : null}
        </div>
      </section>
    </div>
  );
};
